import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  BookOpen, Users, MessageCircle, ChevronLeft, Search, 
  Eye, User, Clock, Filter, RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatObservationPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("group");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [activitySummary, setActivitySummary] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // Filter states
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass && activeTab === "group") {
      fetchGroupChat(selectedClass);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedStudent && activeTab === "private") {
      fetchPrivateChat(selectedStudent);
    }
  }, [selectedStudent]);

  const fetchInitialData = async () => {
    try {
      const [activityRes, studentsRes] = await Promise.all([
        axios.get(`${API}/admin/chats/all-activity`),
        axios.get(`${API}/students`)
      ]);
      
      setActivitySummary(activityRes.data);
      setStudents(studentsRes.data);
      
      // Extract unique classes
      const uniqueClasses = [...new Set(studentsRes.data.map(s => s.class_name))].filter(Boolean).sort();
      setClasses(uniqueClasses);
      
      // Auto-select first class
      if (uniqueClasses.length > 0) {
        setSelectedClass(uniqueClasses[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupChat = async (className) => {
    try {
      const response = await axios.get(`${API}/admin/chats/group/${encodeURIComponent(className)}`);
      setGroupMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error fetching group chat:", error);
      setGroupMessages([]);
    }
  };

  const fetchPrivateChat = async (studentId) => {
    try {
      const response = await axios.get(`${API}/admin/chats/private/${studentId}`);
      setPrivateMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error fetching private chat:", error);
      setPrivateMessages([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : "Inconnu";
  };

  const filteredGroupMessages = groupMessages.filter(msg => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      msg.content?.toLowerCase().includes(query) ||
      msg.student_name?.toLowerCase().includes(query)
    );
  });

  const filteredPrivateMessages = privateMessages.filter(msg => {
    if (!searchQuery) return true;
    return msg.content?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Eye className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="chat-observation-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/director")}
            className="text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo">Observation Pédagogique</span>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={fetchInitialData}
          className="border-white text-white hover:bg-white hover:text-[#1A365D]"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Actualiser
        </Button>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Suivi Pédagogique des Conversations
          </h1>
          <p className="text-[#64748B]">
            Consultez les échanges des élèves avec SophIA pour suivre leur progression
          </p>
        </div>

        {/* Activity Summary */}
        {activitySummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-[#1A365D] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A365D]">{activitySummary.group_messages}</p>
                <p className="text-xs text-[#64748B]">Messages groupe</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <MessageCircle className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A365D]">{activitySummary.private_messages}</p>
                <p className="text-xs text-[#64748B]">Messages privés</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A365D]">{activitySummary.period_days}j</p>
                <p className="text-xs text-[#64748B]">Période analysée</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A365D]">{activitySummary.total_messages}</p>
                <p className="text-xs text-[#64748B]">Total échanges</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Card className="bg-white">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Conversations
                </CardTitle>
                <CardDescription>
                  Visualisez les échanges de groupe et individuels
                </CardDescription>
              </div>
              
              {/* Search */}
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="group" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Chat de groupe
                </TabsTrigger>
                <TabsTrigger value="private" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Chat privé
                </TabsTrigger>
              </TabsList>

              {/* Group Chat Tab */}
              <TabsContent value="group">
                <div className="mb-4">
                  <Select value={selectedClass} onValueChange={setSelectedClass}>
                    <SelectTrigger className="w-full md:w-64">
                      <SelectValue placeholder="Sélectionner une classe" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((className) => (
                        <SelectItem key={className} value={className}>
                          {className}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="h-[500px] border rounded-lg p-4 bg-[#F8F9FA]">
                  {filteredGroupMessages.length === 0 ? (
                    <div className="text-center py-12 text-[#64748B]">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun message dans cette classe</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredGroupMessages.map((msg, index) => (
                        <div 
                          key={msg.id || index}
                          className={`p-3 rounded-lg ${
                            msg.role === "student" 
                              ? "bg-white border border-[#E2E8F0]" 
                              : "bg-[#1A365D]/5 border border-[#1A365D]/20"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {msg.role === "student" ? (
                                <Badge variant="outline" className="text-[#1A365D]">
                                  <User className="w-3 h-3 mr-1" />
                                  {msg.student_name || "Élève"}
                                </Badge>
                              ) : (
                                <Badge className="bg-[#D4AF37] text-white">
                                  <BookOpen className="w-3 h-3 mr-1" />
                                  {msg.philosopher_id || "SophIA"}
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-[#64748B]">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-[#0F172A] whitespace-pre-wrap">
                            {msg.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Private Chat Tab */}
              <TabsContent value="private">
                <div className="mb-4">
                  <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                    <SelectTrigger className="w-full md:w-80">
                      <SelectValue placeholder="Sélectionner un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name} ({student.class_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="h-[500px] border rounded-lg p-4 bg-[#F8F9FA]">
                  {!selectedStudent ? (
                    <div className="text-center py-12 text-[#64748B]">
                      <User className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Sélectionnez un élève pour voir ses conversations</p>
                    </div>
                  ) : filteredPrivateMessages.length === 0 ? (
                    <div className="text-center py-12 text-[#64748B]">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucune conversation privée pour cet élève</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredPrivateMessages.map((msg, index) => (
                        <div 
                          key={msg.id || index}
                          className={`p-3 rounded-lg ${
                            msg.role === "user" 
                              ? "bg-white border border-[#E2E8F0] ml-8" 
                              : "bg-[#1A365D]/5 border border-[#1A365D]/20 mr-8"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge 
                              variant={msg.role === "user" ? "outline" : "default"}
                              className={msg.role === "user" ? "text-[#1A365D]" : "bg-[#D4AF37]"}
                            >
                              {msg.role === "user" ? (
                                <>
                                  <User className="w-3 h-3 mr-1" />
                                  Élève
                                </>
                              ) : (
                                <>
                                  <BookOpen className="w-3 h-3 mr-1" />
                                  {msg.philosopher || "SophIA"}
                                </>
                              )}
                            </Badge>
                            <span className="text-xs text-[#64748B]">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-[#0F172A] whitespace-pre-wrap">
                            {msg.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Most Active Classes */}
        {activitySummary?.most_active_classes?.length > 0 && (
          <Card className="mt-6 bg-white">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Classes les plus actives
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {activitySummary.most_active_classes.map((cls, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedClass(cls.class_name);
                      setActiveTab("group");
                    }}
                    className="border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
                  >
                    {cls.class_name}
                    <Badge className="ml-2 bg-[#D4AF37]">{cls.message_count}</Badge>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ChatObservationPage;
